import { useState, useEffect, useCallback } from 'react';
import { billingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend, Area, AreaChart } from 'recharts';
import { CurrencyInr, Receipt, ArrowUp, ArrowDown, Users, ShoppingCart, ChartBar, Funnel, Eye, Warning, TrendUp } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);
const fmtD = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);
const PIE_COLORS = ['#0B1B3D', '#0F4C81', '#EAB308', '#8B5CF6', '#EC4899', '#0F4C81', '#06B6D4'];

export default function BillingAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('this_month');
  const [drillClinic, setDrillClinic] = useState(null);
  const [drillInvoices, setDrillInvoices] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingAPI.adminGetAnalytics({ period });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const openDrill = async (clinicCode) => {
    setDrillClinic(clinicCode);
    setDrillLoading(true);
    try {
      const res = await billingAPI.adminGetInvoices({ clinic_code: clinicCode, page_size: 50 });
      setDrillInvoices(res.data.invoices || []);
    } catch (e) { console.error(e); }
    finally { setDrillLoading(false); }
  };

  if (loading && !data) return (
    <div className="p-6 flex items-center justify-center min-h-[60vh]">
      <div className="inline-block w-8 h-8 border-3 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const s = data?.summary || {};
  const aging = data?.outstanding_aging || {};

  return (
    <div className="space-y-6" data-testid="billing-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-[#0F172A]">Billing Analytics</h1>
          <p className="text-sm text-[#64748B]">
            {data?.period?.start} to {data?.period?.end}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px] rounded-xl" data-testid="analytics-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_fy">This Financial Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="rounded-xl" data-testid="analytics-refresh">
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards — revenue computed exactly like the Dashboard (inter-branch excluded) */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <KPICard label="Total Revenue" value={`₹${fmt(s.total_revenue)}`} sub={`${s.total_invoices || 0} invoices`} icon={CurrencyInr} color="emerald" testId="kpi-total-revenue" />
        <KPICard label="Net Revenue" value={`₹${fmt(s.net_revenue)}`} sub="+ SO − CN" icon={TrendUp} color="green" testId="kpi-net-revenue" />
        <KPICard label="Collected" value={`₹${fmt(s.total_collected)}`} sub={`${s.collection_rate || 0}% rate`} icon={ArrowUp} color="green" testId="kpi-collected" />
        <KPICard label="Outstanding" value={`₹${fmt(s.total_outstanding)}`} sub="Balance due" icon={ArrowDown} color="red" testId="kpi-outstanding" />
        <KPICard label="Credit Notes" value={`₹${fmt(s.total_cn_amount)}`} sub={`${s.total_credit_notes || 0} notes`} icon={Receipt} color="amber" testId="kpi-credit-notes" />
        <KPICard label="Sales Orders" value={`₹${fmt(s.total_order_value)}`} sub={`${s.total_orders || 0} orders`} icon={ShoppingCart} color="blue" testId="kpi-sales-orders" />
      </div>

      {/* Inter-branch transfers — internal, excluded from revenue (shown for transparency) */}
      <div className="bg-white border border-gray-200 rounded-2xl px-5 py-3 flex items-center justify-between" data-testid="billing-inter-branch-line">
        <span className="text-xs text-[#64748B]">Inter-branch Transfers <span className="text-gray-400">(internal — excluded from revenue)</span> · {s.inter_branch_count || 0} entries</span>
        <span className="text-sm font-semibold text-gray-500" data-testid="billing-inter-branch-total">₹{fmt(s.inter_branch_transfers)}</span>
      </div>

      {/* Revenue Trend + Payment Modes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A] flex items-center gap-2">
              <ChartBar size={18} /> Daily Revenue Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.daily_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.daily_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`₹${fmt(v)}`, 'Revenue']} labelFormatter={l => `Date: ${l}`} />
                  <Area type="monotone" dataKey="revenue" stroke="#0B1B3D" fill="#0B1B3D" fillOpacity={0.12} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-gray-400 text-center py-12">No data for this period</p>}
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A] flex items-center gap-2">
              <Funnel size={18} /> Payment Modes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.payment_modes?.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={data.payment_modes} dataKey="amount" nameKey="mode" cx="50%" cy="50%" outerRadius={70} label={({ mode, percent }) => `${mode} ${(percent * 100).toFixed(0)}%`}>
                      {data.payment_modes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => `₹${fmt(v)}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {data.payment_modes.map((p, i) => (
                    <div key={p.mode} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="capitalize text-[#64748B]">{p.mode}</span>
                      </div>
                      <span className="font-medium text-[#0F172A]">₹{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-sm text-gray-400 text-center py-12">No data</p>}
          </CardContent>
        </Card>
      </div>

      {/* Clinic Performance Table */}
      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A]">Clinic Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="table-default w-full" data-testid="clinic-performance-table">
              <thead>
                <tr>
                  <th className="text-left">Clinic</th>
                  <th className="text-right">Revenue</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Collected</th>
                  <th className="text-right">Outstanding</th>
                  <th className="text-right">Collection %</th>
                  <th className="text-right">GST</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(data?.clinic_breakdown || []).map(c => (
                  <tr key={c.clinic_code} className="hover:bg-[#F8FAFC] transition-colors">
                    <td>
                      <div>
                        <p className="font-medium text-[#0F172A]">{c.clinic_name}</p>
                        <p className="text-xs text-[#64748B]">{c.clinic_code}</p>
                      </div>
                    </td>
                    <td className="text-right font-semibold text-[#0B1B3D]">₹{fmt(c.revenue)}</td>
                    <td className="text-right">{c.invoices}</td>
                    <td className="text-right text-green-700">₹{fmt(c.collected)}</td>
                    <td className="text-right text-red-600">{c.outstanding > 0 ? `₹${fmt(c.outstanding)}` : '-'}</td>
                    <td className="text-right">
                      <Badge variant="outline" className={c.collection_rate >= 80 ? 'bg-green-50 text-green-700 border-green-200' : c.collection_rate >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}>
                        {c.collection_rate}%
                      </Badge>
                    </td>
                    <td className="text-right text-[#64748B]">₹{fmt(c.tax)}</td>
                    <td>
                      <Button variant="ghost" size="sm" onClick={() => openDrill(c.clinic_code)} data-testid={`drill-${c.clinic_code}`}>
                        <Eye size={16} />
                      </Button>
                    </td>
                  </tr>
                ))}
                {(!data?.clinic_breakdown?.length) && (
                  <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No clinic data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Top Customers + Outstanding Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A] flex items-center gap-2">
              <Users size={18} /> Top Customers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data?.top_customers || []).slice(0, 10).map((tc, i) => (
                <div key={tc.name} className="flex items-center justify-between py-1.5 border-b border-gray-50">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-bold text-[#64748B] w-5">{i + 1}</span>
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{tc.name}</p>
                      <p className="text-xs text-[#64748B]">{tc.invoices} invoices | {tc.phone || '-'}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-[#0B1B3D]">₹{fmt(tc.total)}</span>
                </div>
              ))}
              {!data?.top_customers?.length && <p className="text-sm text-gray-400 text-center py-8">No customers</p>}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A] flex items-center gap-2">
              <Warning size={18} /> Outstanding Aging
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={[
                { bucket: '0-30 days', amount: aging["0_30"] || 0 },
                { bucket: '30-60 days', amount: aging["30_60"] || 0 },
                { bucket: '60-90 days', amount: aging["60_90"] || 0 },
                { bucket: '90+ days', amount: aging["90_plus"] || 0 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `₹${fmt(v)}`} />
                <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                  <Cell fill="#22C55E" />
                  <Cell fill="#F59E0B" />
                  <Cell fill="#0F4C81" />
                  <Cell fill="#EF4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-4 gap-2 mt-4">
              {[
                { label: '0-30d', val: aging["0_30"], color: 'bg-green-100 text-green-800' },
                { label: '30-60d', val: aging["30_60"], color: 'bg-amber-100 text-amber-800' },
                { label: '60-90d', val: aging["60_90"], color: 'bg-orange-100 text-orange-800' },
                { label: '90d+', val: aging["90_plus"], color: 'bg-red-100 text-red-800' },
              ].map(a => (
                <div key={a.label} className={`text-center p-2 rounded-lg ${a.color}`}>
                  <p className="text-xs font-medium">{a.label}</p>
                  <p className="text-sm font-bold">₹{fmt(a.val)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GST Summary */}
      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Plus_Jakarta_Sans'] text-[#0F172A]">GST Collection Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-[#F8FAFC] rounded-xl p-4 text-center">
              <p className="text-xs text-[#64748B] mb-1">CGST</p>
              <p className="text-xl font-bold text-[#0B1B3D]">₹{fmt(data?.gst_summary?.cgst)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-xl p-4 text-center">
              <p className="text-xs text-[#64748B] mb-1">SGST</p>
              <p className="text-xl font-bold text-[#0B1B3D]">₹{fmt(data?.gst_summary?.sgst)}</p>
            </div>
            <div className="bg-[#F8FAFC] rounded-xl p-4 text-center">
              <p className="text-xs text-[#64748B] mb-1">IGST</p>
              <p className="text-xl font-bold text-[#0B1B3D]">₹{fmt(data?.gst_summary?.igst)}</p>
            </div>
            <div className="bg-[#0B1B3D] rounded-xl p-4 text-center text-white">
              <p className="text-xs opacity-70 mb-1">Total GST</p>
              <p className="text-xl font-bold">₹{fmt(data?.gst_summary?.total_tax)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drill-down Dialog */}
      <Dialog open={!!drillClinic} onOpenChange={o => { if (!o) setDrillClinic(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoices — {drillClinic}</DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="py-8 text-center"><div className="inline-block w-6 h-6 border-2 border-[#0B1B3D] border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table-default w-full text-sm">
                <thead>
                  <tr>
                    <th>Invoice #</th><th>Customer</th><th>Date</th><th className="text-right">Amount</th><th className="text-right">Balance</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {drillInvoices.map(inv => (
                    <tr key={inv.id}>
                      <td className="font-medium">{inv.invoice_number}</td>
                      <td>{inv.customer?.name || '-'}</td>
                      <td>{inv.date}</td>
                      <td className="text-right font-medium">₹{fmtD(inv.grand_total)}</td>
                      <td className="text-right">{inv.balance_due > 0 ? <span className="text-red-600">₹{fmtD(inv.balance_due)}</span> : '-'}</td>
                      <td><Badge variant="outline" className="text-xs">{inv.payment_status}</Badge></td>
                    </tr>
                  ))}
                  {!drillInvoices.length && <tr><td colSpan={6} className="text-center py-4 text-gray-400">No invoices</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPICard({ label, value, sub, icon: Icon, color, testId }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
  };
  return (
    <Card className={`border rounded-2xl ${colorMap[color] || 'border-gray-200'}`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon size={18} weight="bold" />
          <span className="text-xs font-medium opacity-70">{label}</span>
        </div>
        <p className="text-xl font-bold">{value}</p>
        <p className="text-xs opacity-60 mt-0.5">{sub}</p>
      </CardContent>
    </Card>
  );
}
